import { useEffect, useState } from "react";
import staticData from "../assets/nigeriauniversitydata.json";

import { API_BASE_URL } from "../config/api";

// Normalize static JSON into the same shape the API returns: { name, acronym }
const staticFallback = staticData.universities
  .map((u) => ({ name: u["Name of Institution"], acronym: u.Acronym || null }))
  .sort((a, b) => a.name.localeCompare(b.name));

/**
 * Fetches all Nigerian universities from the live API across all paginated pages.
 * Falls back to the bundled static JSON if BASE_URL is not configured or the
 * request fails. Returns a stable { universities, loading } tuple.
 */
export function useUniversities() {
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!API_BASE_URL) {
      setUniversities(staticFallback);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchAll() {
      try {
        // First page  also tells us how many pages exist
        const res = await fetch(`${API_BASE_URL}/universities?per_page=100`);
        if (!res.ok) throw new Error("API error");
        const json = await res.json();

        const firstPage = json.data || [];
        const lastPage = json.pagination?.last_page ?? 1;

        let all = [...firstPage];

        if (lastPage > 1) {
          const remaining = await Promise.all(
            Array.from({ length: lastPage - 1 }, (_, i) =>
              fetch(`${API_BASE_URL}/universities?per_page=100&page=${i + 2}`)
                .then((r) => r.json())
                .then((j) => j.data || [])
            )
          );
          remaining.forEach((page) => { all = all.concat(page); });
        }

        const normalized = all
          .map((u) => ({ name: u.name, acronym: u.acronym || null }))
          .sort((a, b) => a.name.localeCompare(b.name));

        if (!cancelled) setUniversities(normalized);
      } catch {
        if (!cancelled) setUniversities(staticFallback);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAll();
    return () => { cancelled = true; };
  }, []);

  return { universities, loading };
}
